/**
 * ReportGenerationContext
 * Background tracking of reports being generated.
 * - Persistent loading toast per report (visible even after page change)
 * - Poll /status every 5s until completed/failed
 * - Toast success or error at the end
 * - Callback registerOnComplete() to refresh the list
 */

import React, { createContext, useContext, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { generatedReports } from '../services/api/client';

const ReportGenerationContext = createContext(null);

export function ReportGenerationProvider({ children }) {
  // Map reportId → { toastId, intervalId, name, type }
  const tracked = useRef(new Map());
  // Callbacks called when any report completes
  const onCompleteCallbacks = useRef(new Set());

  const stopTracking = useCallback((reportId) => {
    const entry = tracked.current.get(reportId);
    if (entry) {
      clearInterval(entry.intervalId);
      tracked.current.delete(reportId);
    }
  }, []);

  const trackReport = useCallback((reportId, reportName, reportType) => {
    if (!reportId || tracked.current.has(reportId)) return;

    const label = reportName || `Rapport ${reportType || ''}`.trim();

    // Toast loading persistant
    const toastId = toast.loading(`Generation in progress: ${label}…`, {
      duration: Infinity,
      style: { maxWidth: 400 },
    });

    // Poll toutes les 5s
    const intervalId = setInterval(async () => {
      try {
        const status = await generatedReports.getStatus(reportId);
        const state = status?.status;

        if (state === 'completed') {
          toast.dismiss(toastId);
          toast.success(`Report ready: ${label}`, { duration: 6000 });
          stopTracking(reportId);
          onCompleteCallbacks.current.forEach(cb => { try { cb(reportId, 'completed'); } catch {} });
        } else if (state === 'failed') {
          toast.dismiss(toastId);
          toast.error(`Generation failed: ${label}`, { duration: 8000 });
          stopTracking(reportId);
          onCompleteCallbacks.current.forEach(cb => { try { cb(reportId, 'failed'); } catch {} });
        }
        // pending / processing → continuer le poll
      } catch {
        // Temporary network error → continue
      }
    }, 5000);

    tracked.current.set(reportId, { toastId, intervalId, name: label, type: reportType });
  }, [stopTracking]);

  const registerOnComplete = useCallback((callback) => {
    onCompleteCallbacks.current.add(callback);
    return () => onCompleteCallbacks.current.delete(callback);
  }, []);

  return (
    <ReportGenerationContext.Provider value={{ trackReport, registerOnComplete }}>
      {children}
    </ReportGenerationContext.Provider>
  );
}

export function useReportGeneration() {
  const ctx = useContext(ReportGenerationContext);
  if (!ctx) throw new Error('useReportGeneration must be used inside ReportGenerationProvider');
  return ctx;
}

export default ReportGenerationContext;
